import { useState } from "react";
import {
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors } from "../../../style";
import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import { axisProps, heatRamp } from "./theme";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MARGIN = { top: 4, right: 8, bottom: 0, left: 0 };
const Y_AXIS_WIDTH = 40;
const X_AXIS_HEIGHT = 24;
const GAP = 3;

const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

// Recharts has no heatmap, so this is a category x category scatter whose
// points are drawn as cells sized from the measured plot area.
function BusyHoursHeatmap({ hours, style }) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  // Crop to the hours that saw calls so overnight dead time doesn't squash the grid.
  const active = hours.filter((h) => h.calls > 0).map((h) => h.hour);
  const first = Math.min(...active, 8);
  const last = Math.max(...active, 18);
  const hourRange = Array.from(
    { length: last - first + 1 },
    (_, i) => first + i,
  );

  const data = hours
    .filter((h) => h.hour >= first && h.hour <= last)
    .map((h) => ({ ...h, day: DAYS[h.weekday - 1] }));
  const max = Math.max(...data.map((d) => d.calls), 1);
  const bucket = (calls) =>
    calls === 0
      ? 0
      : Math.min(
          heatRamp.length - 1,
          Math.ceil((calls / max) * (heatRamp.length - 1)),
        );

  const cellW =
    (size.width - MARGIN.left - MARGIN.right - Y_AXIS_WIDTH) / hourRange.length;
  const cellH =
    (size.height - MARGIN.top - MARGIN.bottom - X_AXIS_HEIGHT) / DAYS.length;

  const peak = data.reduce(
    (a, b) => (b.calls > a.calls ? b : a),
    data[0] || {},
  );

  return (
    <ChartCard
      title="Busiest times"
      subtitle={
        peak?.calls
          ? `Calls by day and hour. Peak: ${peak.day} ${hourLabel(peak.hour)} (${peak.calls} calls)`
          : "Calls by day and hour"
      }
      height={260}
      style={style}
      legend={<ScaleLegend max={max} />}
      table={{
        columns: ["Day", ...hourRange.map(hourLabel)],
        rows: DAYS.map((day) => [
          day,
          ...hourRange.map(
            (h) => data.find((d) => d.day === day && d.hour === h)?.calls ?? 0,
          ),
        ]),
      }}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        onResize={(width, height) => setSize({ width, height })}
      >
        <ScatterChart margin={MARGIN}>
          <XAxis
            type="category"
            dataKey="hour"
            allowDuplicatedCategory={false}
            ticks={hourRange}
            tickFormatter={(h) => (h % 2 === 0 ? hourLabel(h) : "")}
            height={X_AXIS_HEIGHT}
            {...axisProps}
            axisLine={false}
          />
          <YAxis
            type="category"
            dataKey="day"
            allowDuplicatedCategory={false}
            ticks={DAYS}
            reversed
            width={Y_AXIS_WIDTH}
            {...axisProps}
            axisLine={false}
          />
          <Tooltip
            cursor={false}
            content={
              <ChartTooltip
                title={(_, payload) => {
                  const p = payload[0].payload;
                  return `${p.day} ${hourLabel(p.hour)}-${hourLabel(p.hour + 1)}`;
                }}
                rows={(payload) => [
                  {
                    label: "Calls",
                    value: payload[0].payload.calls,
                    color: heatRamp[bucket(payload[0].payload.calls)],
                  },
                ]}
              />
            }
          />
          <Scatter
            data={data}
            isAnimationActive={false}
            shape={({ cx, cy, payload }) =>
              cellW > 0 && cellH > 0 ? (
                <rect
                  x={cx - cellW / 2 + GAP / 2}
                  y={cy - cellH / 2 + GAP / 2}
                  width={cellW - GAP}
                  height={cellH - GAP}
                  rx={4}
                  fill={heatRamp[bucket(payload.calls)]}
                />
              ) : null
            }
          />
        </ScatterChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function ScaleLegend({ max }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        marginBottom: "10px",
        color: colors.textLight,
        fontSize: "12px",
      }}
    >
      0
      {heatRamp.map((c) => (
        <span
          key={c}
          style={{
            width: "18px",
            height: "8px",
            borderRadius: "2px",
            background: c,
          }}
        />
      ))}
      {max} {max === 1 ? "call" : "calls"}
    </div>
  );
}

export default BusyHoursHeatmap;
